// The microphone as a 16 kHz mono WAV, for dictation where the browser has no speech
// recognition of its own (components/bots/DictateButton.jsx; the server writes it down,
// backend utils/dictation.py). MediaRecorder records in the browser's own format, the
// browser decodes it and an OfflineAudioContext mixes it to one channel at 16 kHz, which
// is all speech needs (32 kB a second).

const RATE = 16000;

export const canRecord = () => typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
  && typeof window.MediaRecorder !== 'undefined' && typeof window.OfflineAudioContext !== 'undefined';

/** Starts recording; rejects when the microphone is not allowed or missing.
 * Resolves to { stop() -> Promise<Blob | null> (null: nothing was recorded), cancel() }. */
export async function startRecording() {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const release = () => stream.getTracks().forEach((track) => track.stop());
  let recorder;
  try {
    recorder = new MediaRecorder(stream);
  } catch (e) {
    release();
    throw e;
  }
  const parts = [];
  recorder.ondataavailable = (e) => { if (e.data?.size) parts.push(e.data); };
  const stopped = new Promise((resolve) => { recorder.onstop = resolve; });
  recorder.start();
  return {
    async stop() {
      if (recorder.state !== 'inactive') recorder.stop();
      await stopped;
      release();
      return parts.length ? toWav(new Blob(parts, { type: recorder.mimeType })) : null;
    },
    cancel() {
      if (recorder.state !== 'inactive') recorder.stop();
      release();
    },
  };
}

export async function toWav(blob) {
  const Context = window.AudioContext || window.webkitAudioContext;
  const context = new Context();
  let audio;
  try {
    audio = await context.decodeAudioData(await blob.arrayBuffer());
  } finally {
    context.close?.();
  }
  const offline = new OfflineAudioContext(1, Math.max(1, Math.ceil(audio.duration * RATE)), RATE);
  const source = offline.createBufferSource();
  source.buffer = audio;
  source.connect(offline.destination);
  source.start();
  return encodeWav((await offline.startRendering()).getChannelData(0), RATE);
}

/** 16-bit PCM WAV of mono samples (-1..1). */
export function encodeWav(samples, rate) {
  const view = new DataView(new ArrayBuffer(44 + samples.length * 2));
  const ascii = (at, text) => [...text].forEach((c, i) => view.setUint8(at + i, c.charCodeAt(0)));
  ascii(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  ascii(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  samples.forEach((s, i) => view.setInt16(44 + i * 2, Math.round(Math.max(-1, Math.min(1, s)) * 0x7fff), true));
  return new Blob([view], { type: 'audio/wav' });
}
